import React, { useState } from "react";
import "./Header.css";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { useAuth } from "../contexts/AuthContext";
import LoadingOverlay from "./LoadingOverlay";

type Link = {
  path: string;
  label: string;
  tfAdminOnly?: boolean;
  publicAdminOrSystemAdminOnly?: boolean;
  systemAdminOnly?: boolean;
};

type DomainConfig = {
  icon: string;
  title: string;
  links: (
    userId: number | null,
    isTfAdmin: boolean,
    isPublicAdmin: boolean,
    isSystemAdmin: boolean
  ) => Link[];
};

const domainConfig: Record<string, DomainConfig> = {
  rm: {
    icon: "/ラケモン.svg",
    title: "ラケモンApp",
    links: () => [
      { path: "/rm/home", label: "Home" },
      { path: "/rm/create-match", label: "試合作成" },
      { path: "/rm/matches", label: "試合" },
      { path: "/rm/users", label: "メンバー管理" },
    ],
  },
  tf: {
    icon: "/スキルシート.svg",
    title: "スキルマネジメント",
    links: (userId, isTfAdmin) => {
      const allLinks: Link[] = [
        { path: "/tf/user-sheets", label: "ホーム" },
        { path: `/tf/my-sheet/${userId}`, label: "スキルシート" },
        {
          path: "/tf/viewing-permission",
          label: "基本設定",
          tfAdminOnly: true,
        },
      ];
      return allLinks.filter((link) => !link.tfAdminOnly || isTfAdmin);
    },
  },
  default: {
    icon: "/ユーザー管理.svg",
    title: "ユーザー設定",
    links: (userId, isTfAdmin, isPublicAdmin, isSystemAdmin) =>
      [
        { path: `/main/profile/${userId}`, label: "プロフィール" },
        {
          path: "/main/admin",
          label: "ユーザー管理",
          publicAdminOrSystemAdminOnly: true,
        },
        {
          path: "/main/system-admin",
          label: "マスタメンテ",
          systemAdminOnly: true,
        },
      ].filter(
        (link) =>
          (!link.publicAdminOrSystemAdminOnly ||
            isPublicAdmin ||
            isSystemAdmin) &&
          (!link.systemAdminOnly || isSystemAdmin)
      ),
  },
};

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { authUser, setUser, isFetchingUser } = useUser();
  const { setIsAuthenticated } = useAuth();

  const currentDomain =
    Object.keys(domainConfig).find((domain) =>
      location.pathname.startsWith(`/${domain}`)
    ) || "default";

  const { icon, title, links } =
    domainConfig[currentDomain as keyof typeof domainConfig];
  const userId = authUser?.id || null;
  const isTfAdmin = authUser?.isTfAdmin || false;
  const isPublicAdmin = authUser?.isPublicAdmin || false;
  const isSystemAdmin = authUser?.isSystemAdmin || false;

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/logout`,
        {},
        { withCredentials: true }
      );
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem("authUser");
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleProfileChange = () => {
    navigate(`/main/profile/${userId}`);
  };

  const getLinkClass = (path: string) => {
    const basePath = path.replace(/\/\d+$/, ""); // 数字で終わるユーザーIDを除去
    const currentBasePath = location.pathname.replace(/\/\d+$/, "");
    return currentBasePath.startsWith(basePath)
      ? "header-nav-item active"
      : "header-nav-item";
  };

  if (isFetchingUser) {
    return <LoadingOverlay />;
  }

  return (
    <>
      {isLoggingOut && <LoadingOverlay />}
      <header>
        <div className="header-left">
          <img src={icon} alt="アプリアイコン" className="header-icon" />
          <span className="header-title">{title}</span>
        </div>

        <div className="header-divider"></div>

        <nav className="header-nav">
          {links(userId, isTfAdmin, isPublicAdmin, isSystemAdmin).map(
            (link) => (
              <a
                href={link.path}
                className={getLinkClass(link.path)}
                key={link.path}
              >
                {link.label}
              </a>
            )
          )}
        </nav>

        <div className="header-divider"></div>

        <div
          className="header-right relative cursor-pointer"
          onClick={() => setDropdownOpen(!dropdownOpen)}
        >
          <img
            src={`${authUser?.profileImage}`}
            alt="プロフィール画像"
            className="header-profile-image rounded-full"
          />
          <span className="header-profile-name">
            {authUser ? authUser.name : "アカウント名"}
          </span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`header-dropdown-icon transition-transform duration-300 ${
              dropdownOpen ? "rotate-180" : ""
            }`}
          />
          {dropdownOpen && (
            <div className="absolute right-8 mt-32 bg-white border rounded shadow-lg w-48 z-10">
              <button
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  handleProfileChange();
                }}
              >
                プロフィール修正
              </button>
              <button
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLogout();
                }}
              >
                ログアウト
              </button>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
